import { PackageType } from '@frontend/package/types';
import { SettingKey } from '@frontend/setting/types';
import { ScreenTrigger, ScreenType, Step } from '@frontend/wizard/types';
import { useEffect, useRef, useState } from 'react';

import { useScreen } from '../useScreen';

interface ViewProps {
    showDropOff: boolean;
    showPickUp: boolean;
    isSkipping: boolean;
    dropOffRequireProofOfIdentity: boolean;
    pickUpRequireProofOfIdentity: boolean;
    setDropOffRequireProofOfIdentity: (value: boolean) => void;
    setPickUpRequireProofOfIdentity: (value: boolean) => void;
    submitDisabled: boolean;
    onSubmit: () => void;
    onBack: (type: ScreenType) => void;
}

const useProveIdentity = (props: { wizard_id: string }): ViewProps => {
    const { store, onBack, updateWizardData, onSubmit: navigateToNextStep } = useScreen(props.wizard_id);
    const skipExecutedRef = useRef(false);

    const dropPackageWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.DROP_OFF_PACKAGE_WORKFLOW_ID)?.value;
    const pickPackageWorkflowId = store?.functionalitySettings.find((setting) => setting.key === SettingKey.PICK_UP_PACKAGE_WORKFLOW_ID)?.value;

    const showDropOff = typeof dropPackageWorkflowId === 'string' && dropPackageWorkflowId.length > 0;
    const showPickUp = typeof pickPackageWorkflowId === 'string' && pickPackageWorkflowId.length > 0;
    const hasPackageWorkflows = showDropOff || showPickUp;

    const [dropOffRequireProofOfIdentity, setDropOffRequireProofOfIdentityState] = useState<boolean>(store?.data.dropOffRequireProofOfIdentity ?? false);
    const [pickUpRequireProofOfIdentity, setPickUpRequireProofOfIdentityState] = useState<boolean>(store?.data.pickUpRequireProofOfIdentity ?? false);

    const dropOffRequireProofRef = useRef(dropOffRequireProofOfIdentity);
    const pickUpRequireProofRef = useRef(pickUpRequireProofOfIdentity);

    useEffect(() => {
        dropOffRequireProofRef.current = dropOffRequireProofOfIdentity;
    }, [dropOffRequireProofOfIdentity]);

    useEffect(() => {
        pickUpRequireProofRef.current = pickUpRequireProofOfIdentity;
    }, [pickUpRequireProofOfIdentity]);

    useEffect(() => {
        if (store?.data.dropOffRequireProofOfIdentity !== undefined) {
            setDropOffRequireProofOfIdentityState(store.data.dropOffRequireProofOfIdentity);
        }
        if (store?.data.pickUpRequireProofOfIdentity !== undefined) {
            setPickUpRequireProofOfIdentityState(store.data.pickUpRequireProofOfIdentity);
        }
    }, [store?.data.dropOffRequireProofOfIdentity, store?.data.pickUpRequireProofOfIdentity]);

    const findNextStep = (anyRequireProof: boolean): Step | undefined => {
        if (!store) return undefined;

        const conditionalTrigger = anyRequireProof ? ScreenTrigger.SUBMIT_TRUE : ScreenTrigger.SUBMIT_FALSE;
        const conditionalStep = store.steps.find((step) => step.screen === ScreenType.PROVE_IDENTITY && step.trigger === conditionalTrigger);
        if (conditionalStep) return conditionalStep;

        return store.steps.find((step) => step.screen === ScreenType.PROVE_IDENTITY && step.trigger === ScreenTrigger.SUBMIT);
    };

    const buildPendingPackages = (dropProof: boolean, pickProof: boolean) => [
        ...(showDropOff
            ? [
                  {
                      type: PackageType.DROP_OFF,
                      workflow_id: dropPackageWorkflowId as string,
                      require_proof_of_identity: dropProof
                  }
              ]
            : []),
        ...(showPickUp
            ? [
                  {
                      type: PackageType.PICK_UP,
                      workflow_id: pickPackageWorkflowId as string,
                      require_proof_of_identity: pickProof
                  }
              ]
            : [])
    ];

    const persistProofSettings = (dropProof: boolean, pickProof: boolean) => {
        updateWizardData({
            dropOffRequireProofOfIdentity: showDropOff ? dropProof : false,
            pickUpRequireProofOfIdentity: showPickUp ? pickProof : false,
            pendingPackages: buildPendingPackages(dropProof, pickProof)
        });
    };

    const setDropOffRequireProofOfIdentity = (value: boolean) => {
        setDropOffRequireProofOfIdentityState(value);
        persistProofSettings(value, pickUpRequireProofRef.current);
    };

    const setPickUpRequireProofOfIdentity = (value: boolean) => {
        setPickUpRequireProofOfIdentityState(value);
        persistProofSettings(dropOffRequireProofRef.current, value);
    };

    const onSubmit = () => {
        if (!store?.data.transaction) return;

        const dropProof = dropOffRequireProofRef.current;
        const pickProof = pickUpRequireProofRef.current;
        const anyRequireProof = (showDropOff && dropProof) || (showPickUp && pickProof);

        persistProofSettings(dropProof, pickProof);
        navigateToNextStep(findNextStep(anyRequireProof));
    };

    useEffect(() => {
        if (hasPackageWorkflows || !store?.data.transaction || !store.functionalitySettings || skipExecutedRef.current) return;

        skipExecutedRef.current = true;
        persistProofSettings(false, false);
        navigateToNextStep(findNextStep(false));
    }, [hasPackageWorkflows, store?.data.transaction, store?.functionalitySettings]);

    return {
        showDropOff,
        showPickUp,
        isSkipping: !hasPackageWorkflows,
        dropOffRequireProofOfIdentity,
        pickUpRequireProofOfIdentity,
        setDropOffRequireProofOfIdentity,
        setPickUpRequireProofOfIdentity,
        submitDisabled: !store?.data.transaction,
        onSubmit,
        onBack
    };
};

export default useProveIdentity;
